import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomTags from "../../components/CustomTags";
import KeywordBox from "../../components/KeywordBox";
import AudioService from "./AudioService";
import { formatAudioBytes, tagsToCommaInput } from "./audioUtils";

function defaultDisplayName(filename) {
    if (!filename) return "";
    const base = String(filename).replace(/^.*[/\\]/, "");
    const dot = base.lastIndexOf(".");
    return dot > 0 ? base.slice(0, dot) : base;
}

function uploadCategoryForLibrary(library) {
    const fromApi = String(library?.category || "").trim().toUpperCase();
    if (fromApi === "EFFECT" || fromApi === "BGM") return fromApi;
    const key = `${library?.id || ""} ${library?.label || ""}`.toLowerCase();
    if (key.includes("bgm") || key.includes("music") || key.includes("background")) return "BGM";
    return "EFFECT";
}

function buildRowId(libraryId, relativePath) {
    return `${libraryId}::${relativePath}`;
}

const QUICK_TAGS = ["Jigsaw", "Sliding", "Word Wheel", "All apps"];

const LOCAL_SEARCH_KEY = "admin.audio.local.searchOptions";
const LIST_FILTERS = ["all", "unsuitable", "readyToReview", "uploaded"];
const SIZE_FILTERS = ["all", "under10kb", "over50kb", "over2mb", "over4mb"];
const EMPTY_EXCLUDE_LIBRARY_IDS = [];
const TEN_KB = 10 * 1024;
const FIFTY_KB = 50 * 1024;
const TWO_MB = 2 * 1024 * 1024;
const FOUR_MB = 4 * 1024 * 1024;

function fileMatchesListFilter(file, listFilter) {
    const unsuitable = Boolean(file?.unsuitable);
    const uploaded = Boolean(file?.uploaded);
    const readyToReview = Boolean(file?.readyToReview);
    if (listFilter === "unsuitable") return unsuitable && !uploaded;
    if (listFilter === "readyToReview") return readyToReview && !uploaded;
    if (listFilter === "uploaded") return uploaded;
    return !unsuitable && !uploaded && !readyToReview;
}

function fileMatchesSizeFilter(file, sizeFilter) {
    const size = Number(file?.size);
    if (sizeFilter === "under10kb") return Number.isFinite(size) && size < TEN_KB;
    if (sizeFilter === "over50kb") return Number.isFinite(size) && size > FIFTY_KB;
    if (sizeFilter === "over2mb") return Number.isFinite(size) && size > TWO_MB;
    if (sizeFilter === "over4mb") return Number.isFinite(size) && size > FOUR_MB;
    return true;
}

function fileMatchesFilters(file, listFilter, sizeFilter) {
    return fileMatchesListFilter(file, listFilter) && fileMatchesSizeFilter(file, sizeFilter);
}

function folderFromPath(path) {
    const normalized = String(path || "").replace(/\\/g, "/");
    const slash = normalized.lastIndexOf("/");
    return slash > 0 ? normalized.slice(0, slash) : "";
}

const FOLDER_LABELS = {
    GCLB: "Gaming / Chillwave",
    LFSF: "Lo-Fi Study / Focus",
    RDEM: "Rainy Day / Emotional",
    SLMF: "Sleep / Meditation",
    VLPB: "Vlog / Podcast Background",
};

function folderLabel(folder) {
    const top = String(folder || "").split("/")[0];
    const hint = FOLDER_LABELS[top];
    return hint ? `${top} · ${hint}` : folder;
}

function loadLocalSearchOptions(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return { keyword: "", listFilter: "all", sizeFilter: "all", folder: "all", libraryId: "" };
        }
        const parsed = JSON.parse(raw);
        let listFilter = typeof parsed?.listFilter === "string" ? parsed.listFilter : "";
        if (!LIST_FILTERS.includes(listFilter)) {
            listFilter = parsed?.showUnsuitable ? "unsuitable" : "all";
        }
        const sizeFilter =
            typeof parsed?.sizeFilter === "string" && SIZE_FILTERS.includes(parsed.sizeFilter)
                ? parsed.sizeFilter
                : "all";
        return {
            keyword: typeof parsed?.keyword === "string" ? parsed.keyword : "",
            listFilter,
            sizeFilter,
            folder: typeof parsed?.folder === "string" && parsed.folder ? parsed.folder : "all",
            libraryId: typeof parsed?.libraryId === "string" ? parsed.libraryId : "",
        };
    } catch {
        return { keyword: "", listFilter: "all", sizeFilter: "all", folder: "all", libraryId: "" };
    }
}

function saveLocalSearchOptions(storageKey, options) {
    try {
        localStorage.setItem(storageKey, JSON.stringify(options));
    } catch {
        /* ignore quota / private mode */
    }
}

export default function AdminAudioLocalTab({
    libraryId = null,
    excludeLibraryIds = EMPTY_EXCLUDE_LIBRARY_IDS,
} = {}) {
    const searchStorageKey = libraryId
        ? `${LOCAL_SEARCH_KEY}.${libraryId}`
        : LOCAL_SEARCH_KEY;
    const initialSearch = useMemo(
        () => loadLocalSearchOptions(searchStorageKey),
        [searchStorageKey]
    );
    const excludeLibraryKey = useMemo(
        () => (excludeLibraryIds || []).map(String).join("\0"),
        [excludeLibraryIds]
    );
    const [libraries, setLibraries] = useState([]);
    const [loadingLibraries, setLoadingLibraries] = useState(true);
    const [libraryTab, setLibraryTab] = useState(0);
    const [keyword, setKeyword] = useState(initialSearch.keyword);
    const [listFilter, setListFilter] = useState(initialSearch.listFilter);
    const [sizeFilter, setSizeFilter] = useState(initialSearch.sizeFilter);
    const [folderFilter, setFolderFilter] = useState(initialSearch.folder || "all");
    const [selectedRow, setSelectedRow] = useState(null);

    const [name, setName] = useState("");
    const [comment, setComment] = useState("");
    const [tags, setTags] = useState([]);
    const [tagOptions, setTagOptions] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [unsuitable, setUnsuitable] = useState(false);
    const [readyToReview, setReadyToReview] = useState(false);
    const [savingUnsuitable, setSavingUnsuitable] = useState(false);
    const [savingReadyToReview, setSavingReadyToReview] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    const previewAudioRef = useRef(null);
    const previewUrlRef = useRef(null);
    const restoredLibraryTabRef = useRef(false);
    const hasLoadedLibrariesRef = useRef(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewPlaying, setPreviewPlaying] = useState(false);
    const [hasPlayedPreview, setHasPlayedPreview] = useState(false);

    const activeLibrary = libraries[libraryTab] || null;

    const loadLibraries = useCallback(async () => {
        if (!hasLoadedLibrariesRef.current) {
            setLoadingLibraries(true);
        }
        const result = await AudioService.listLocalLibraries();
        if (!result.success) {
            setLoadingLibraries(false);
            setSeverity("error");
            setMessage(result.error || "Failed to load local audio libraries");
            setOpenSnackbar(true);
            setLibraries([]);
            return;
        }
        const list = Array.isArray(result.data?.libraries) ? result.data.libraries : [];
        const exclude = new Set(
            excludeLibraryKey ? excludeLibraryKey.split("\0").filter(Boolean) : []
        );
        const filtered = list.filter((lib) => {
            if (libraryId) return lib.id === libraryId;
            if (exclude.size === 0) return true;
            return !exclude.has(lib.id);
        });
        setLibraries(filtered);
        hasLoadedLibrariesRef.current = true;
        setLoadingLibraries(false);
    }, [libraryId, excludeLibraryKey]);

    useEffect(() => {
        loadLibraries();
    }, [loadLibraries]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setTagsLoading(true);
            const result = await AudioService.listDistinctTags();
            if (cancelled) return;
            const rows = result.success && Array.isArray(result.data) ? result.data : [];
            setTagOptions(rows.map((row) => row.tag));
            setTagsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!libraries.length) return;
        if (!restoredLibraryTabRef.current) {
            restoredLibraryTabRef.current = true;
            const savedIdx = initialSearch.libraryId
                ? libraries.findIndex((lib) => lib.id === initialSearch.libraryId)
                : -1;
            if (savedIdx >= 0) {
                setLibraryTab(savedIdx);
                return;
            }
        }
        if (libraryTab >= libraries.length) {
            setLibraryTab(0);
        }
    }, [libraries, initialSearch.libraryId, libraryTab]);

    useEffect(() => {
        if (!libraries.length) return;
        saveLocalSearchOptions(searchStorageKey, {
            keyword,
            listFilter,
            sizeFilter,
            folder: folderFilter,
            libraryId: libraries[libraryTab]?.id || "",
        });
    }, [keyword, listFilter, sizeFilter, folderFilter, libraryTab, libraries, searchStorageKey]);

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        const audio = previewAudioRef.current;
        if (audio) {
            audio.pause();
            audio.removeAttribute("src");
        }
        setPreviewPlaying(false);
    }, []);

    useEffect(() => revokePreviewUrl, [revokePreviewUrl]);

    const folderOptions = useMemo(() => {
        const folders = new Set();
        (activeLibrary?.files || []).forEach((file) => {
            const folder = folderFromPath(file.path);
            if (folder) folders.add(folder);
        });
        return Array.from(folders).sort();
    }, [activeLibrary]);

    const effectiveFolder =
        folderFilter !== "all" && folderOptions.includes(folderFilter) ? folderFilter : "all";

    const filteredFiles = useMemo(() => {
        if (!activeLibrary?.files) return [];
        const q = keyword.trim().toLowerCase();
        const files = activeLibrary.files
            .filter((file) => fileMatchesFilters(file, listFilter, sizeFilter))
            .filter((file) => effectiveFolder === "all" || folderFromPath(file.path) === effectiveFolder)
            .map((file) => ({
                ...file,
                id: buildRowId(activeLibrary.id, file.path),
                libraryId: activeLibrary.id,
                folder: folderFromPath(file.path),
                category: uploadCategoryForLibrary(activeLibrary),
            }));
        if (!q) return files;
        return files.filter(
            (file) =>
                String(file.name || "").toLowerCase().includes(q) ||
                String(file.path || "").toLowerCase().includes(q) ||
                String(file.folder || "").toLowerCase().includes(q)
        );
    }, [activeLibrary, keyword, listFilter, sizeFilter, effectiveFolder]);

    const handleSelectRow = (row) => {
        if (!row) {
            revokePreviewUrl();
            setSelectedRow(null);
            setUnsuitable(false);
            setReadyToReview(false);
            setHasPlayedPreview(false);
            return;
        }
        revokePreviewUrl();
        setSelectedRow(row);
        setName(defaultDisplayName(row.name || row.path));
        setComment("");
        setUnsuitable(Boolean(row.unsuitable));
        setReadyToReview(Boolean(row.readyToReview));
        setHasPlayedPreview(false);
    };

    const addQuickTag = (label) => {
        const key = String(label).toLowerCase();
        setTags((prev) => {
            if (prev.some((tag) => String(tag).toLowerCase() === key)) return prev;
            return [...prev, label];
        });
    };

    const patchLocalFile = (libraryId, relativePath, patch, updateSelected = true) => {
        setLibraries((prev) =>
            prev.map((lib) => {
                if (lib.id !== libraryId) return lib;
                return {
                    ...lib,
                    files: (lib.files || []).map((file) =>
                        file.path === relativePath ? { ...file, ...patch } : file
                    ),
                };
            })
        );
        if (!updateSelected) return;
        setSelectedRow((prev) =>
            prev && prev.libraryId === libraryId && prev.path === relativePath
                ? { ...prev, ...patch }
                : prev
        );
    };

    const nextVisibleRow = (currentId) => {
        const idx = filteredFiles.findIndex((file) => file.id === currentId);
        if (idx >= 0 && idx < filteredFiles.length - 1) return filteredFiles[idx + 1];
        if (idx > 0) return filteredFiles[idx - 1];
        return null;
    };

    const applyReviewResult = (checked, nextRow, patch) => {
        const advance = Boolean(nextRow);
        patchLocalFile(selectedRow.libraryId, selectedRow.path, patch, !advance);
        if (checked && listFilter === "all") {
            handleSelectRow(nextRow);
        }
    };

    const handleUnsuitableChange = async (checked) => {
        if (!selectedRow) return;
        const nextRow = checked && listFilter === "all" ? nextVisibleRow(selectedRow.id) : null;
        setUnsuitable(checked);
        if (checked) setReadyToReview(false);
        setSavingUnsuitable(true);
        const result = await AudioService.setLocalUnsuitable({
            libraryId: selectedRow.libraryId,
            relativePath: selectedRow.path,
            unsuitable: checked,
        });
        setSavingUnsuitable(false);
        if (!result.success) {
            setUnsuitable(!checked);
            setReadyToReview(Boolean(selectedRow.readyToReview));
            setSeverity("error");
            setMessage(result.error || "Failed to update unsuitable flag");
            setOpenSnackbar(true);
            return;
        }
        const updatedAt = result.data?.updatedAt || new Date().toISOString();
        applyReviewResult(checked, nextRow, {
            unsuitable: checked,
            readyToReview: checked ? false : Boolean(result.data?.readyToReview),
            updatedAt,
        });
    };

    const handleReadyToReviewChange = async (checked) => {
        if (!selectedRow) return;
        const nextRow = checked && listFilter === "all" ? nextVisibleRow(selectedRow.id) : null;
        setReadyToReview(checked);
        if (checked) setUnsuitable(false);
        setSavingReadyToReview(true);
        const result = await AudioService.setLocalReadyToReview({
            libraryId: selectedRow.libraryId,
            relativePath: selectedRow.path,
            readyToReview: checked,
        });
        setSavingReadyToReview(false);
        if (!result.success) {
            setReadyToReview(!checked);
            setUnsuitable(Boolean(selectedRow.unsuitable));
            setSeverity("error");
            setMessage(result.error || "Failed to update ready-to-review flag");
            setOpenSnackbar(true);
            return;
        }
        const updatedAt = result.data?.updatedAt || new Date().toISOString();
        applyReviewResult(checked, nextRow, {
            readyToReview: checked,
            unsuitable: checked ? false : Boolean(result.data?.unsuitable),
            updatedAt,
        });
    };

    const handlePreviewToggle = async () => {
        if (!selectedRow) return;
        if (previewPlaying) {
            previewAudioRef.current?.pause();
            setPreviewPlaying(false);
            return;
        }
        setPreviewLoading(true);
        try {
            revokePreviewUrl();
            const blob = await AudioService.previewLocalLibraryBlob(
                selectedRow.libraryId,
                selectedRow.path
            );
            const url = URL.createObjectURL(blob);
            previewUrlRef.current = url;
            if (!previewAudioRef.current) {
                previewAudioRef.current = new Audio();
                previewAudioRef.current.onended = () => setPreviewPlaying(false);
            }
            const audio = previewAudioRef.current;
            audio.src = url;
            audio.currentTime = 0;
            await audio.play();
            setPreviewPlaying(true);
            setHasPlayedPreview(true);
        } catch (e) {
            setSeverity("error");
            setMessage(e?.message || "Failed to preview local file");
            setOpenSnackbar(true);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedRow) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setSeverity("error");
            setMessage("Name is required");
            setOpenSnackbar(true);
            return;
        }
        if (!selectedRow.uploadable) {
            setSeverity("error");
            setMessage("This file format is not supported for upload");
            setOpenSnackbar(true);
            return;
        }
        if (unsuitable) {
            setSeverity("error");
            setMessage("Unsuitable files are not uploaded to the cloud");
            setOpenSnackbar(true);
            return;
        }
        setUploading(true);
        const result = await AudioService.uploadFromLocalLibrary({
            libraryId: selectedRow.libraryId,
            relativePath: selectedRow.path,
            name: trimmedName,
            category: uploadCategoryForLibrary(activeLibrary) || selectedRow.category,
            tags: tagsToCommaInput(tags),
            source: comment.trim() || null,
        });
        setUploading(false);
        if (result.success) {
            const shouldAdvance = listFilter === "all" || listFilter === "readyToReview";
            const nextRow = shouldAdvance ? nextVisibleRow(selectedRow.id) : null;
            patchLocalFile(
                selectedRow.libraryId,
                selectedRow.path,
                { uploaded: true, updatedAt: new Date().toISOString() },
                !nextRow
            );
            setSeverity("success");
            setMessage(`Uploaded "${trimmedName}" to cloud`);
            setOpenSnackbar(true);
            if (shouldAdvance) {
                handleSelectRow(nextRow);
            }
        } else {
            setSeverity("error");
            setMessage(result.error || "Upload failed");
            setOpenSnackbar(true);
        }
    };

    const columns = useMemo(() => {
        const cols = [];
        if (folderOptions.length > 0) {
            cols.push({
                field: "folder",
                headerName: "Folder",
                flex: 1,
                valueFormatter: (value) => folderLabel(value) || value,
            });
        }
        cols.push(
            { field: "name", headerName: "Name", flex: 2 },
            {
                field: "size",
                headerName: "Size",
                flex: 1,
                valueGetter: (_value, row) => Number(row?.size),
                valueFormatter: (value) => formatAudioBytes(value),
            },
            {
                field: "uploadable",
                headerName: "Upload",
                flex: 1,
                valueFormatter: (value) => (value ? "Yes" : "No"),
            }
        );
        return cols;
    }, [folderOptions.length]);

    if (loadingLibraries) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (libraries.length === 0) {
        return (
            <Alert severity="info">
                {libraryId
                    ? "This local audio library is not configured or its folder was not found. Add it under app.local-audio-libraries and restart puzzle-be."
                    : (
                        <>
                            No local audio libraries are configured. Add paths under{" "}
                            <code>app.local-audio-libraries</code> in the backend profile (e.g.{" "}
                            <code>application-local.yml</code>).
                        </>
                    )}
            </Alert>
        );
    }

    return (
        <Box>
            {!libraryId && (
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={libraryTab}
                    onChange={(_, value) => {
                        setLibraryTab(value);
                        revokePreviewUrl();
                        setSelectedRow(null);
                        setTags([]);
                    }}
                    variant="fullWidth"
                    aria-label="Local audio libraries"
                >
                    {libraries.map((lib, index) => {
                        const visibleCount = (lib.files || []).filter((file) =>
                            fileMatchesFilters(file, listFilter, sizeFilter)
                        ).length;
                        return (
                            <Tab
                                key={lib.id}
                                label={`${lib.label || lib.id} (${visibleCount})`}
                                value={index}
                                disabled={lib.available === false}
                            />
                        );
                    })}
                </Tabs>
            </Box>
            )}

            {activeLibrary && activeLibrary.available === false && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Library folder not found on this machine: {activeLibrary.root}
                </Alert>
            )}

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "stretch",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <KeywordBox
                            initialValue={initialSearch.keyword}
                            callback={setKeyword}
                            placeholder="Filter by name or path"
                            size="small"
                            sx={{ minWidth: 240 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="local-audio-list-filter-label">List</InputLabel>
                            <Select
                                labelId="local-audio-list-filter-label"
                                label="List"
                                value={listFilter}
                                onChange={(e) => {
                                    setListFilter(e.target.value);
                                    revokePreviewUrl();
                                    setSelectedRow(null);
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="unsuitable">Unsuitable</MenuItem>
                                <MenuItem value="readyToReview">Ready to review</MenuItem>
                                <MenuItem value="uploaded">Uploaded</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id="local-audio-size-filter-label">Size</InputLabel>
                            <Select
                                labelId="local-audio-size-filter-label"
                                label="Size"
                                value={sizeFilter}
                                onChange={(e) => {
                                    setSizeFilter(e.target.value);
                                    revokePreviewUrl();
                                    setSelectedRow(null);
                                }}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="under10kb">Less 10KB</MenuItem>
                                <MenuItem value="over50kb">Over 50KB</MenuItem>
                                <MenuItem value="over2mb">Over 2MB</MenuItem>
                                <MenuItem value="over4mb">Over 4MB</MenuItem>
                            </Select>
                        </FormControl>
                        {folderOptions.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="local-audio-folder-filter-label">Folder</InputLabel>
                                <Select
                                    labelId="local-audio-folder-filter-label"
                                    label="Folder"
                                    value={effectiveFolder}
                                    onChange={(e) => {
                                        setFolderFilter(e.target.value);
                                        revokePreviewUrl();
                                        setSelectedRow(null);
                                    }}
                                >
                                    <MenuItem value="all">All folders</MenuItem>
                                    {folderOptions.map((folder) => (
                                        <MenuItem key={folder} value={folder}>
                                            {folderLabel(folder)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                    <DataGrid
                        rows={filteredFiles}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loadingLibraries}
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        disableRowSelectionOnClick
                        onRowClick={(params) => handleSelectRow(params.row)}
                        getRowClassName={(params) =>
                            selectedRow?.id === params.id ? "local-audio-row-selected" : ""
                        }
                        sx={{
                            flex: 1,
                            width: "100%",
                            minHeight: 420,
                            bgcolor: "background.paper",
                            "& .MuiDataGrid-row.local-audio-row-selected": {
                                bgcolor: "secondary.light",
                                color: "#fff",
                                "&:hover": {
                                    bgcolor: "secondary.light",
                                },
                            },
                            "& .MuiDataGrid-row.local-audio-row-selected .MuiDataGrid-cell": {
                                bgcolor: "secondary.light",
                                color: "#fff",
                                borderColor: "secondary.light",
                            },
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        border: "3px solid",
                        borderColor: "secondary.light",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.25,
                            bgcolor: "secondary.light",
                            color: "#fff",
                        }}
                    >
                        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                            Upload to cloud
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                    {!selectedRow ? (
                        <Typography variant="body2" color="text.secondary">
                            Select a file from the list to preview and upload.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                {selectedRow.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatAudioBytes(selectedRow.size)} · {activeLibrary?.category}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        previewLoading ? (
                                            <CircularProgress size={16} />
                                        ) : previewPlaying ? (
                                            <StopIcon />
                                        ) : (
                                            <PlayArrowIcon />
                                        )
                                    }
                                    onClick={handlePreviewToggle}
                                    disabled={previewLoading}
                                >
                                    {previewPlaying ? "Stop" : "Play"}
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={unsuitable}
                                            onChange={(e) => handleUnsuitableChange(e.target.checked)}
                                            disabled={
                                                !hasPlayedPreview ||
                                                savingUnsuitable ||
                                                savingReadyToReview ||
                                                uploading
                                            }
                                        />
                                    }
                                    label="Unsuitable"
                                    sx={{ mr: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={readyToReview}
                                            onChange={(e) => handleReadyToReviewChange(e.target.checked)}
                                            disabled={
                                                !hasPlayedPreview ||
                                                savingUnsuitable ||
                                                savingReadyToReview ||
                                                uploading
                                            }
                                        />
                                    }
                                    label="Ready to review"
                                    sx={{ mr: 0 }}
                                />
                            </Stack>
                            <TextField
                                label="Name"
                                size="small"
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                            <CustomTags
                                value={tags}
                                onChange={(_e, newTags) => setTags(newTags)}
                                options={tagOptions}
                                loading={tagsLoading}
                                label="Tags"
                                placeholder="Search or add tags…"
                                helperText="Pick an existing tag or type a new one and press Enter."
                                disabled={uploading}
                                margin="none"
                                variant="outlined"
                            />
                            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                                {QUICK_TAGS.map((label) => {
                                    const selected = tags.some(
                                        (tag) => String(tag).toLowerCase() === label.toLowerCase()
                                    );
                                    return (
                                        <Button
                                            key={label}
                                            size="small"
                                            variant={selected ? "contained" : "outlined"}
                                            onClick={() => addQuickTag(label)}
                                            disabled={uploading}
                                        >
                                            {label}
                                        </Button>
                                    );
                                })}
                            </Stack>
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                <TextField
                                    label="Comment"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Optional note or attribution"
                                />
                                <Button
                                    size="small"
                                    variant={
                                        String(comment).toLowerCase().includes("test required")
                                            ? "contained"
                                            : "outlined"
                                    }
                                    disabled={uploading}
                                    onClick={() => {
                                        const label = "Test required";
                                        setComment((prev) => {
                                            const text = String(prev || "");
                                            if (text.toLowerCase().includes(label.toLowerCase())) {
                                                return text;
                                            }
                                            return text.trim() ? `${text.trim()}\n${label}` : label;
                                        });
                                    }}
                                    sx={{ whiteSpace: "nowrap", mt: 0.5 }}
                                >
                                    Test required
                                </Button>
                            </Stack>
                            <Button
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                                onClick={handleUpload}
                                disabled={
                                    uploading ||
                                    !hasPlayedPreview ||
                                    unsuitable ||
                                    Boolean(selectedRow.uploaded) ||
                                    !selectedRow.uploadable
                                }
                            >
                                Upload to cloud
                            </Button>
                            {selectedRow.uploaded ? (
                                <Typography variant="caption" color="text.secondary">
                                    This file is already uploaded.
                                </Typography>
                            ) : null}
                            {!selectedRow.uploadable && (
                                <Typography variant="caption" color="error">
                                    Unsupported format for this library category.
                                </Typography>
                            )}
                        </Stack>
                    )}
                    </Box>
                </Box>
            </Box>

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
